import React, { useState } from 'react';
import { Box, Container, Typography, Paper, Grid, Card, Button, Chip, useTheme, alpha } from '@mui/material';
import {
  DisplaySettings,
  IntegrationInstructions,
  People,
  Palette,
  Add,
  CheckCircle,
  EmojiEvents,
  Restaurant,
  Construction,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import ContactFormModal from './ContactFormModal';
import { pricingContent } from '../content/pages/pricing';

// Declare global for runtime config
declare global {
  interface Window {
    __APP_CONFIG__?: {
      API_BASE_URL: string;
      ADMIN_URL?: string;
      ENVIRONMENT?: string;
    };
  }
}

// Icon mapping for additional products
const ProductIconMap: Record<string, React.ReactElement> = {
  EmojiEvents: <EmojiEvents sx={{ fontSize: 48, color: 'primary.main' }} />,
  Restaurant: <Restaurant sx={{ fontSize: 48, color: 'primary.main' }} />,
};

const CustomIconMap: Record<string, React.ReactElement> = {
  People: <People sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />,
  IntegrationInstructions: <IntegrationInstructions sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />,
  DisplaySettings: <DisplaySettings sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
};

const Pricing: React.FC = () => {
  const c = pricingContent;
  const theme = useTheme();
  const [contactModalOpen, setContactModalOpen] = useState(false);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{
        color: 'white',
        py: 8,
        bgcolor: 'primary.main',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Beaver Background Image */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/beaver-happy-family.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.15,
          zIndex: 0,
        }} />

        {/* Navigation */}
        <Navigation />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {c.hero.title}
            </Typography>
            <Typography variant="h4" component="h2" sx={{ opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
              {c.hero.subtitle}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Pricing Summary - The Main Event */}
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper
          elevation={6}
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            borderRadius: 4,
            border: 3,
            borderColor: 'primary.main',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Accent bar */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            bgcolor: 'primary.main'
          }} />

          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mt: 1 }}>
            {c.pricingSummary.title}
          </Typography>

          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
            {c.pricingSummary.tagline}
          </Typography>

          {/* Big Price */}
          <Typography variant="h1" component="div" sx={{
            fontWeight: 800,
            color: 'primary.main',
            fontSize: { xs: '4rem', md: '5rem' },
            lineHeight: 1,
            mb: 1,
          }}>
            {c.pricingSummary.basePrice}
            <Typography component="span" variant="h5" sx={{ fontWeight: 400, color: 'text.secondary' }}>
              {c.pricingSummary.basePriceLabel}
            </Typography>
          </Typography>

          {/* Highlights Grid */}
          <Grid container spacing={2} sx={{ my: 4 }}>
            {c.pricingSummary.highlights.map((highlight, index) => (
              <Grid item xs={6} key={index}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}>
                  <CheckCircle sx={{
                    fontSize: 24,
                    color: highlight.emphasis ? 'primary.main' : 'success.main'
                  }} />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: highlight.emphasis ? 700 : 500,
                      color: highlight.emphasis ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {highlight.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Additional Screens */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.secondary.main, 0.08),
              borderColor: alpha(theme.palette.secondary.main, 0.3),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Add sx={{ fontSize: 28, color: 'secondary.main' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                {c.pricingSummary.additionalScreens.price}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {c.pricingSummary.additionalScreens.label}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {c.pricingSummary.additionalScreens.description}
            </Typography>
          </Paper>

          {/* No Gating Statement */}
          <Typography variant="body1" sx={{
            color: 'primary.main',
            fontWeight: 600,
            mb: 3,
            fontSize: '1.1rem',
          }}>
            {c.pricingSummary.noGating}
          </Typography>

          {/* CTA */}
          <Button
            component={Link}
            to={c.pricingSummary.cta.link}
            variant="contained"
            size="large"
            sx={{
              py: 2,
              px: 6,
              fontSize: '1.2rem',
              fontWeight: 700,
              borderRadius: 2,
            }}
          >
            {c.pricingSummary.cta.text}
          </Button>
        </Paper>
      </Container>

      {/* Additional Products Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            {c.additionalProducts.title}
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
            {c.additionalProducts.subtitle}
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ maxWidth: 900, mx: 'auto' }}>
          {c.additionalProducts.products.map((product, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  boxShadow: 3,
                  bgcolor: 'background.paper',
                  borderRadius: '16px',
                  border: 2,
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  {ProductIconMap[product.iconName]}
                </Box>
                <Typography variant="h5" component="h3" textAlign="center" gutterBottom sx={{ fontWeight: 600 }}>
                  {product.title}
                </Typography>
                <Typography variant="body1" textAlign="center" sx={{ color: 'text.secondary', mb: 2 }}>
                  {product.description}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Chip
                    icon={<Construction />}
                    label={product.status}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Custom Pricing Card */}
        <Box sx={{ mt: 8 }}>
          <Card sx={{
            bgcolor: 'background.paper',
            boxShadow: 3,
            borderRadius: '16px',
            border: 2,
            borderColor: 'primary.main',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              bgcolor: 'primary.main',
            }} />
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                <Palette sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
                <Typography variant="h3" component="h3" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {c.custom.title}
                </Typography>
              </Box>
              <Typography variant="h5" component="h4" sx={{ mb: 3, color: 'text.secondary', maxWidth: 800, mx: 'auto' }}>
                {c.custom.subtitle}
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.6, maxWidth: 900, mx: 'auto' }}>
                {c.custom.description}
              </Typography>

              <Grid container spacing={4} sx={{ mb: 4 }}>
                {c.custom.features.map((feature, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      {CustomIconMap[feature.iconName]}
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Button
                variant="outlined"
                size="large"
                onClick={() => setContactModalOpen(true)}
                sx={{
                  py: 2,
                  px: 6,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: 'secondary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                    borderWidth: 2,
                  },
                }}
              >
                {c.custom.cta}
              </Button>
            </Box>
          </Card>
        </Box>
      </Container>

      {/* Contact Form Modal */}
      <ContactFormModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
      />
    </Box>
  );
};

export default Pricing;
